import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  createAttendanceByDay,
  deleteAttendanceByDay,
  exportAttendanceByDay,
  getAttendanceByDay,
  getAttendanceErrorByRangeDay,
  updateAttendanceByDay,
} from '@/apis/attendance'
import { apiHost } from '@/configs/api'
import useNotification from '@/hooks/useNotification'
import {
  AttendanceRequest,
  CreateAttendanceRequest,
  ExportAttendanceByDayRequest,
  GetAttendanceErrorByRangeDayRequest,
  GetAttendanceErrorByRangeDayResponse,
  GetAttendanceResponse,
  UpdateAttendanceRequest,
} from '@/types/apis/attendance'

export const useAttendance = () => {
  const openNotification = useNotification()
  const { t } = useTranslation()
  const [attendanceLoading, setAttendanceLoading] = useState(false)
  const [attendanceErrorLoading, setAttendanceErrorLoading] = useState(false)
  const [getAttendanceResponse, setAttendanceResponse] = useState<GetAttendanceResponse>()
  const [getAttendanceErrorResponse, setAttendanceErrorResponse] = useState<GetAttendanceErrorByRangeDayResponse>([])
  const [exportAttendanceByDayLoading, setExportAttendanceByDayLoading] = useState(false)
  const [updateAttendanceLoading, setUpdateAttendanceLoading] = useState(false)
  const [createAttendanceLoading, setCreateAttendanceLoading] = useState(false)

  const get = useCallback(async (request: AttendanceRequest) => {
    try {
      setAttendanceLoading(true)
      const res = await getAttendanceByDay(request)
      setAttendanceResponse(res)
    } finally {
      setAttendanceLoading(false)
    }
  }, [])

  const updateAttendance = async (request: UpdateAttendanceRequest) => {
    try {
      setUpdateAttendanceLoading(true)
      await updateAttendanceByDay(request)
      openNotification({
        description: t('pages.attendance.updateSuccessfully'),
      })
    } catch (err) {
      openNotification({
        type: 'error',
        description: t('pages.attendance.updateFail'),
      })
    } finally {
      setUpdateAttendanceLoading(false)
    }
  }

  const createAttendance = async (request: CreateAttendanceRequest) => {
    try {
      setCreateAttendanceLoading(true)
      await createAttendanceByDay(request)
      openNotification({
        description: t('pages.attendance.createSuccessfully'),
      })
    } catch (err) {
      openNotification({
        type: 'error',
        description: t('pages.attendance.createFail'),
      })
    } finally {
      setCreateAttendanceLoading(false)
    }
  }

  const deleteAttendance = async (id: number) => {
    try {
      setCreateAttendanceLoading(true)
      await deleteAttendanceByDay(id)
      openNotification({
        description: t('pages.attendance.deleteSuccessfully'),
      })
    } catch (err) {
      openNotification({
        type: 'error',
        description: t('pages.attendance.deleteFail'),
      })
    } finally {
      setCreateAttendanceLoading(false)
    }
  }

  const getErrorByRangeDay = useCallback(
    async (request: GetAttendanceErrorByRangeDayRequest) => {
      try {
        setAttendanceErrorLoading(true)
        const res = await getAttendanceErrorByRangeDay(request)
        setAttendanceErrorResponse(res)
      } catch {
        openNotification({
          type: 'error',
          description: t('components.pages.clientDevice.listReport.hooks.selectRangeDateFail'),
        })
      } finally {
        setAttendanceErrorLoading(false)
      }
    },
    [openNotification, t]
  )

  const exportExcelAttendanceByRangeDay = async (request: ExportAttendanceByDayRequest) => {
    try {
      setExportAttendanceByDayLoading(true)
      // TODO: call API and open url in a new tab
      const result = await exportAttendanceByDay(request)
      window.open(`${apiHost}${result.url}`, '_blank')
    } catch (err) {
      // TODO:
    } finally {
      setExportAttendanceByDayLoading(false)
    }
  }

  return {
    getAttendance: get,
    attendanceLoading,
    getAttendanceResponse,
    exportAttendanceByDayLoading,
    exportExcelAttendanceByRangeDay,
    updateAttendance,
    updateAttendanceLoading,
    createAttendanceLoading,
    createAttendance,
    getErrorByRangeDay,
    getAttendanceErrorResponse,
    attendanceErrorLoading,
    deleteAttendance,
  }
}
